import { createContext, useContext, useEffect, useId, useSyncExternalStore } from 'react'

import type { HealthSignal } from './types'

/**
 * Доска сигналов связи открытого окна (RP-UI-LINK). Точка «Связь» в шапке одна, а знают о связи экраны: выбор кассы —
 * о кассе, раздел — об источнике. Экран публикует свой сигнал хуком {@link useHealthSignal}, оболочка читает все
 * сигналы доски и рисует одну точку.
 *
 * Почему React-контекст, а не срез store: сигнал — производное от данных экрана (кэш RTK Query, состояние агента),
 * а не намерение человека; дублировать его в slice запрещено (CLAUDE.md §7). Сигнал живёт ровно столько, сколько
 * смонтирован экран, который его сообщил: ушёл с экрана — его строка из мини-окна пропала. Контекст доступен и пакетам
 * (они не знают RootState), и привязкам приложения.
 */
export type HealthActionHandler = (key: string) => void

export interface HealthBoardEntry {
  signal: HealthSignal
  /** Исполнитель действий мини-окна для этого сигнала; `null` — у сигнала нет своих действий. */
  onAction: HealthActionHandler | null
}

export interface HealthBoard {
  /** Сообщить (или снять — `null`) сигнал под ключом публикатора. Тот же сигнал повторно подписчиков не будит. */
  publish: (token: string, signal: HealthSignal | null, onAction?: HealthActionHandler | null) => void
  /**
   * Экран объявляет «связь с сервером я показываю сам» (RP-SALE-FAST, шаг 5): общее окно «Восстанавливаем соединение» на это время не
   * рисуется. Возвращает снятие объявления. Объявлений может быть несколько (вложенные экраны) — окно скрыто, пока есть хоть одно.
   */
  claimLink: (token: string) => () => void
  /** Есть ли экран, объявивший, что сам показывает связь. Примитив: контракт `useSyncExternalStore`. */
  linkClaimed: () => boolean
  /** Выполнить действие мини-окна у сигнала субъекта `subjectId`; `false` — исполнителя нет. */
  act: (subjectId: string, key: string) => boolean
  subscribe: (listener: () => void) => () => void
  /** Сигналы в порядке публикации; ссылка меняется только при изменении (контракт `useSyncExternalStore`). */
  snapshot: () => readonly HealthSignal[]
}

const sameSignal = (a: HealthSignal, b: HealthSignal) => JSON.stringify(a) === JSON.stringify(b)

export function createHealthBoard(): HealthBoard {
  const entries = new Map<string, HealthBoardEntry>()
  const listeners = new Set<() => void>()
  const claims = new Set<string>()
  let signals: readonly HealthSignal[] = []

  const changed = () => {
    signals = [...entries.values()].map((entry) => entry.signal)
    for (const listener of listeners) listener()
  }

  return {
    publish(token, signal, onAction = null) {
      const previous = entries.get(token)
      if (signal === null) {
        if (previous === undefined) return
        entries.delete(token)
        changed()
        return
      }
      entries.set(token, { signal, onAction })
      // Обработчик обновляется молча: экран создаёт его заново на каждой отрисовке, а показывать нечего нового.
      if (previous === undefined || !sameSignal(previous.signal, signal)) changed()
    },
    claimLink(token) {
      claims.add(token)
      for (const listener of listeners) listener()
      return () => {
        if (!claims.delete(token)) return
        for (const listener of listeners) listener()
      }
    },
    linkClaimed: () => claims.size > 0,
    act(subjectId, key) {
      for (const entry of entries.values()) {
        if (entry.signal.subject.id === subjectId && entry.onAction !== null) {
          entry.onAction(key)
          return true
        }
      }
      return false
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    snapshot: () => signals,
  }
}

/** Доска окна; без провайдера (`null`) сигналы экрана никуда не идут — экран работает как обычно. */
export const HealthBoardContext = createContext<HealthBoard | null>(null)

/** Доска окна из контекста; `null` — вне оболочки. */
export function useHealthBoard(): HealthBoard | null {
  return useContext(HealthBoardContext)
}

/**
 * Сообщить сигнал связи своего экрана в точку «Связь» шапки, пока экран смонтирован. `null` — сейчас сообщить нечего
 * (данные ещё не пришли, субъекта нет). `onAction` получает ключ действия из `signal.actions`.
 */
export function useHealthSignal(signal: HealthSignal | null, onAction?: HealthActionHandler): void {
  const board = useHealthBoard()
  const token = useId()
  useEffect(() => {
    board?.publish(token, signal, onAction ?? null)
  }, [board, token, signal, onAction])
  useEffect(() => {
    if (board === null) return undefined
    return () => board.publish(token, null)
  }, [board, token])
}

const NO_SIGNALS: readonly HealthSignal[] = []
const noop = () => () => undefined
const none = () => NO_SIGNALS

/** Все сигналы доски — для точки «Связь» оболочки. */
export function useHealthBoardSignals(board: HealthBoard | null): readonly HealthSignal[] {
  return useSyncExternalStore(board?.subscribe ?? noop, board?.snapshot ?? none)
}

/**
 * Объявить, что этот экран сам показывает связь с сервером, пока смонтирован (RP-SALE-FAST, шаг 5): продажа и окно оплаты рисуют
 * строку связи внутри себя, а общее окно по центру поверх них не появляется. Вне оболочки (доски нет) ничего не делает.
 */
export function useScreenOwnsLink(): void {
  const board = useHealthBoard()
  const token = useId()
  useEffect(() => board?.claimLink(token), [board, token])
}

const noLinkClaim = () => false

/** Какой-то экран объявил, что сам показывает связь (общее окно связи тогда скрыто, а строка связи экрана — видна). */
export function useLinkOwnedByScreen(): boolean {
  const board = useHealthBoard()
  return useSyncExternalStore(board?.subscribe ?? noop, board?.linkClaimed ?? noLinkClaim)
}
